/**
 * Where this tab's session lives.
 *
 * A support session and the operator's own account are two different people
 * signed in to the same origin at the same time, and localStorage is shared by
 * every tab of that origin. So the operator's own tab kept writing its identity
 * back over the impersonated one — `adoc_t` is rewritten by userSlice, `adoc_p`
 * on every profile fetch, and an auto-refresh keeps those firing — and the
 * support tab drifted back to the operator, or ended up with a token the CRM
 * then rejected with 401.
 *
 * sessionStorage is per-tab, so an impersonated session written there cannot see
 * or be seen by any other tab. Members keep using localStorage exactly as before,
 * which is what lets them stay signed in between visits: sessionStorage would log
 * them out every time they closed the tab.
 *
 * Reads therefore check sessionStorage FIRST and fall back to localStorage. That
 * order is the whole mechanism — a tab holding an impersonated session ignores
 * the shared keys entirely.
 */

const TOKEN = "adoc_t";
const PROFILE = "adoc_p";
const IMPERSONATING = "adoc_i";
const AGREEMENTS = "adoc_agreements";

/** Keys that belong to whoever is signed in, and so must move together. */
const IDENTITY_KEYS = [TOKEN, PROFILE, IMPERSONATING, AGREEMENTS];

const safeGet = (store, key) => {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (store, key, value) => {
  try {
    store.setItem(key, value);
  } catch {
    /* private mode / quota — nothing useful to do */
  }
};

const safeRemove = (store, key) => {
  try {
    store.removeItem(key);
  } catch {
    /* ignore */
  }
};

/**
 * Claim the tab as soon as the bundle loads, not when the route handler runs.
 *
 * The redux store is built during module evaluation and seeds itself from
 * `adoc_p`, which happens well before /impersonate/:token gets to call
 * beginImpersonatedSession(). Without this, the first paint of a support
 * session reads the operator's identity out of the shared store.
 */
try {
  if (
    typeof window !== "undefined" &&
    window.location?.pathname?.startsWith("/impersonate/")
  ) {
    safeSet(sessionStorage, IMPERSONATING, "true");
    // Drop the previous occupant's identity in the same breath. Opening a
    // SECOND member from the CRM reuses the tab, so sessionStorage still holds
    // the first one's profile — and the store seeds from it here, before
    // /impersonate/:token gets to call beginImpersonatedSession(). The banner
    // reads the store, so it announced the account support had just left while
    // the screen already showed the new one: the worst possible moment to be
    // told the wrong name. Clearing it costs a blank name for the fraction of a
    // second before the profile lands.
    // The TOKEN goes too, and that is the important one. sessionStorage
    // survives a navigation within the tab, so until /impersonate/:token
    // installs the new one the app is still authenticated as the member
    // support just left — and anything that refreshes the profile on boot
    // fetches THEM, writes their identity back, and wins the race against the
    // fetch for the account actually being opened.
    safeRemove(sessionStorage, TOKEN);
    safeRemove(sessionStorage, PROFILE);
    safeRemove(sessionStorage, AGREEMENTS);
  }
} catch {
  /* no window (SSR/tests) — nothing to claim */
}

/** True when THIS TAB is a support session opened from the CRM. */
export const isPerTabSession = () =>
  safeGet(sessionStorage, IMPERSONATING) === "true";

/**
 * Read a session value: this tab's own session wins over the shared one.
 */
export const readAuth = (key) => {
  const perTab = safeGet(sessionStorage, key);
  if (perTab !== null) return perTab;

  // An impersonated tab must never inherit the operator's shared keys — a missing
  // value here means "not set for this session", not "look next door".
  if (isPerTabSession()) return null;

  return safeGet(localStorage, key);
};

/**
 * Write a session value into whichever store this tab is using.
 */
export const writeAuth = (key, value) => {
  if (isPerTabSession()) {
    safeSet(sessionStorage, key, value);
    return;
  }
  safeSet(localStorage, key, value);
};

export const removeAuth = (key) => {
  // Read the flag first: removing IMPERSONATING itself would otherwise flip this
  // tab back to "shared" mid-call and take the operator's copy with it.
  const perTab = isPerTabSession();
  safeRemove(sessionStorage, key);
  if (!perTab) safeRemove(localStorage, key);
};

/** The bearer token for API calls from this tab. */
export const getAuthToken = () => readAuth(TOKEN) || "";

/**
 * Start a support session that belongs to this tab alone.
 *
 * Marks the tab first, so every later read and write resolves to sessionStorage,
 * then stores the token. The operator's own session in localStorage is left
 * untouched — they stay signed in wherever else they had the app open.
 */
export const beginImpersonatedSession = (token) => {
  safeSet(sessionStorage, IMPERSONATING, "true");
  safeSet(sessionStorage, TOKEN, token);
  safeRemove(sessionStorage, PROFILE);
  safeRemove(sessionStorage, AGREEMENTS);
};

/** Drop this tab's support session, leaving any shared session alone. */
export const endImpersonatedSession = () => {
  IDENTITY_KEYS.forEach((key) => safeRemove(sessionStorage, key));
};

export const AUTH_KEYS = { TOKEN, PROFILE, IMPERSONATING, AGREEMENTS };
